/**
 * BT-2084, M4S-5777 (X83-X86, X88, X91) — a crosstab's weighting: who is weighted, to what, which
 * of its schemes the tables use, and what every table and file says about it. Pure.
 *
 * A scheme names its balancing questions, a method, and optionally a group to weight (`scope`) and
 * a group whose actual split becomes the targets (`matchTo`). Lily's iOOH case is Unexposed
 * weighted to match Exposed on OOHPEXP: Unexposed reads Exposed's split exactly, and Exposed keeps
 * a weight of 1. Both groups are `NestMember` snapshots, never a reference to a variable. Since
 * M4S-5777 a group can be any banner answer or custom-variable bucket (X85), a target can be a
 * custom variable's buckets (X86, `cv:<id>`), and the method 'variable' reads each respondent's
 * weight from a variable (X83).
 *
 * A crosstab holds several named schemes (`WeightSchemeSet`, X83): record state
 * (`ai_layout.state.weighting`, `WeightingRecord`), written beside the `weighted` switch, which
 * stays the only on/off. The record also keeps `weightScheme`, the active scheme alone, so a build
 * from before X83 reopening the record after a rollback still weighs it the same; it is derived
 * here (`weightingRecordOf` / `activeScheme`) and read only to lift a crosstab saved before X83.
 *
 * Every read of a scheme goes through `parseWeightScheme` and every read of the set through
 * `parseWeightingRecord`: a POST body or a stored column can hold anything. Decisions:
 * docs/pdlc/crosstabs-oct6-uat-bugs.md §10.4 and docs/pdlc/crosstabs-weighting.md.
 */

import type {
    CustomVariable,
    NamedWeightScheme,
    NestMember,
    WeightScheme,
    WeightSchemeSet,
    WeightSchemeTarget,
    WeightSchemeTargetSet,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';
import type { ComputeOpts, ComputedTable } from '@/app/(private)/ai/components/Crosstabs/data';

import { CONDITION_RULES } from './conditions';
import { exposureLayer } from './geography-banner';
import { nestSources, parseMember, type NestCatalog } from './nest-variables';
import type { CrosstabQuestionDto } from './question-formatter';
import {
    hasVariableAnswers,
    isVariableTarget,
    variableAnswers,
    variableIdOfTarget,
    CV_TARGET_PREFIX,
} from './variable-members';
import {
    HOUSE_LIMITS,
    efficiencyLine,
    isHouseLimits,
    limitsLine,
    parseWeightLimits,
    type WeightFlag,
    type WeightLimits,
    type WeightStats,
} from './weight-limits';

export type WeightMethod = WeightScheme['method'];

const METHODS: ReadonlySet<string> = new Set(['raked', 'cell', 'static', 'variable']);

/** How each method is named in the dialog, the rail and every file. */
export const METHOD_LABELS: Record<WeightMethod, string> = {
    raked: 'RIM (raking)',
    cell: 'Cell',
    static: 'Single variable (cell)',
    variable: 'Weights from a variable',
};

const isRecord = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);

const MAX_LABEL = 300;
const cleanText = (x: unknown, max = MAX_LABEL): string | null =>
    typeof x === 'string' && x.trim() ? x.trim().slice(0, max) : null;

/** One balancing question, or null when it names nothing or has no target left. */
function parseVariable(raw: unknown): WeightSchemeTarget | null {
    if (!isRecord(raw) || typeof raw.variableId !== 'string' || !raw.variableId) return null;
    if (!isRecord(raw.targets)) return null;
    const targets = Object.fromEntries(
        Object.entries(raw.targets).filter(
            (e): e is [string, number] =>
                typeof e[1] === 'number' && Number.isFinite(e[1]) && e[1] >= 0,
        ),
    );
    if (Object.keys(targets).length === 0) return null;
    const label = cleanText(raw.label);
    const catLabels = isRecord(raw.catLabels)
        ? Object.fromEntries(
              Object.keys(targets).flatMap((cat) => {
                  const l = cleanText((raw.catLabels as Record<string, unknown>)[cat]);
                  return l ? [[cat, l]] : [];
              }),
          )
        : {};
    // X88: the answer codes a kept set's target covers. Each must be a non-empty list of codes.
    const members = isRecord(raw.members)
        ? Object.fromEntries(
              Object.keys(targets).flatMap((cat) => {
                  const m = (raw.members as Record<string, unknown>)[cat];
                  const codes = Array.isArray(m)
                      ? [...new Set(m.filter((c): c is string => typeof c === 'string' && !!c))]
                      : [];
                  return codes.length ? [[cat, codes]] : [];
              }),
          )
        : {};
    return {
        variableId: raw.variableId,
        targets,
        // M4S-5777: snapshots for the disclosure, after the fields a saved scheme already had.
        ...(label ? { label } : {}),
        ...(Object.keys(catLabels).length ? { catLabels } : {}),
        ...(Object.keys(members).length ? { members } : {}),
    };
}

/** An optional group: absent reads as absent, anything else must parse. */
function parseGroup(raw: unknown): NestMember | null | undefined {
    if (raw === undefined || raw === null) return undefined;
    return parseMember(raw);
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A kept target set's snapshot on a scheme (X88), or undefined when it does not parse. */
function parseTargetSetRef(raw: unknown): WeightSchemeTargetSet | undefined {
    if (!isRecord(raw)) return undefined;
    const id = cleanText(raw.id, 64);
    const name = cleanText(raw.name, 120);
    const source = cleanText(raw.source, 200);
    const year = raw.year;
    if (!id || !name || !source || typeof year !== 'number' || !Number.isInteger(year)) {
        return undefined;
    }
    const reviewedOn =
        typeof raw.reviewedOn === 'string' && DATE.test(raw.reviewedOn) ? raw.reviewedOn : null;
    return { id, name, source, year, ...(reviewedOn ? { reviewedOn } : {}) };
}

/** A scheme's id: `ws_` and up to 64 safe characters. */
const SCHEME_ID = /^ws_[A-Za-z0-9_-]{1,64}$/;
const MAX_NAME = 80;

/**
 * A weight scheme read from JSON, or null when there is none to apply: an unknown method, no
 * balancing question with a target (no weight variable, for 'variable'), a malformed group, or a
 * group matched to itself. Built in one key order, so a saved scheme and its Save compare alike;
 * every field M4S-5777 added comes after the ones a scheme saved before it holds, so such a scheme
 * still compares as it did.
 */
export function parseWeightScheme(raw: unknown): WeightScheme | null {
    if (!isRecord(raw)) return null;
    const { method, variables, scope: rawScope, matchTo: rawMatch, rules } = raw;
    if (typeof method !== 'string' || !METHODS.has(method)) return null;
    if (!Array.isArray(variables)) return null;
    const parsed: WeightSchemeTarget[] = [];
    for (const v of variables) {
        const p = parseVariable(v);
        if (p && !parsed.some((x) => x.variableId === p.variableId)) parsed.push(p);
    }
    const weightVariable =
        method === 'variable' && typeof raw.weightVariable === 'string' && raw.weightVariable
            ? raw.weightVariable
            : null;
    if (method === 'variable' ? !weightVariable : parsed.length === 0) return null;
    const scope = parseGroup(rawScope);
    const matchTo = parseGroup(rawMatch);
    if (scope === null || matchTo === null) return null;
    if (scope && matchTo && scope.key === matchTo.key) return null;
    // Weights read from a variable have no targets to match.
    if (method === 'variable' && matchTo) return null;
    const targetSet = parseTargetSetRef(raw.targetSet);
    const limits = parseWeightLimits(raw.limits);
    const id = typeof raw.id === 'string' && SCHEME_ID.test(raw.id) ? raw.id : null;
    const name = cleanText(raw.name, MAX_NAME);
    return {
        method: method as WeightMethod,
        variables: method === 'variable' ? [] : parsed,
        ...(scope ? { scope } : {}),
        ...(matchTo ? { matchTo } : {}),
        // M4S-5777: last, so a scheme saved before it compares as it did.
        ...(rules === CONDITION_RULES ? { rules: CONDITION_RULES } : {}),
        ...(weightVariable ? { weightVariable } : {}),
        ...(targetSet ? { targetSet } : {}),
        ...(limits && !isHouseLimits(limits) ? { limits } : {}),
        ...(id ? { id } : {}),
        ...(name ? { name } : {}),
    };
}

const SURVEY_QID = '__SURVEY_ID';

/** What a target variable is called in a note: its snapshot label, the wave, or its id. */
function targetName(v: WeightSchemeTarget): string {
    if (v.variableId === SURVEY_QID) return 'Survey / Wave';
    if (isVariableTarget(v.variableId)) return v.label ?? variableIdOfTarget(v.variableId);
    return v.variableId;
}

/**
 * The line that says what the weighting does, for the rail, the legend, the "Weighted" chip and
 * every export: "Unexposed weighted to match Exposed on OOHPEXP". Static reads only its first
 * question, so only that one is named. A scheme saved before M4S-5777 reads exactly as it did.
 */
export function weightingNote(scheme: WeightScheme): string {
    const who = scheme.scope?.label ?? 'All respondents';
    if (scheme.method === 'variable') {
        return `${who} weighted by ${scheme.weightVariable ?? 'a variable'} (weights from a variable)`;
    }
    const vars = scheme.method === 'static' ? scheme.variables.slice(0, 1) : scheme.variables;
    const qids = vars.map(targetName).join(', ');
    const to = scheme.matchTo ? `to match ${scheme.matchTo.label}` : 'to targets';
    const how =
        scheme.method === 'cell' ? ' (cell-based)' : scheme.method === 'static' ? ' (static)' : '';
    return `${who} weighted ${to} on ${qids}${how}`;
}

// ── What every table and file says (X91) ────────────────────────────────────

/** What a weighted table says about its weighting: carried from the scheme to the compute. */
export interface WeightingDisclosure {
    /** "Weighted (RIM (raking)) — Gen pop: All respondents weighted to targets on AGE, GENDER". */
    headline: string;
    /** One line per balancing variable: its targets, or the group whose split it matches. */
    targets: string[];
    /** Where the targets came from (a kept target set, X88), when they did. */
    source?: string;
    /** The limits it is flagged against: the house limits unless the scheme carries its own. */
    limits: WeightLimits;
    /** The group weighted, when only one is (decision 7: the Total then stays unweighted). */
    scope?: string;
}

/**
 * Said on every table and in every file of a crosstab that is not weighted (X91). An opt-in
 * sample as it came is described for what it is: never as representative of a population.
 */
export const UNWEIGHTED_DISCLOSURE =
    'Unweighted (natural fallout): every respondent counts once, so the figures describe the people who took part, not a wider population.';

const pct = (x: number) => `${Math.round(x * 10) / 10}%`;

/** The disclosure for a scheme (X91). */
export function weightingDisclosure(scheme: WeightScheme): WeightingDisclosure {
    const name = scheme.name ? ` — ${scheme.name}` : '';
    const headline = `Weighted (${METHOD_LABELS[scheme.method]})${name}: ${weightingNote(scheme)}`;
    const vars = scheme.method === 'static' ? scheme.variables.slice(0, 1) : scheme.variables;
    const targets =
        scheme.method === 'variable'
            ? [`Each respondent's weight is read from ${scheme.weightVariable ?? 'a variable'}.`]
            : vars.map((v) => {
                  const label = v.label ?? targetName(v);
                  if (scheme.matchTo) return `${label}: matched to ${scheme.matchTo.label}'s split`;
                  const shares = Object.entries(v.targets)
                      .map(([cat, share]) => `${v.catLabels?.[cat] ?? cat} ${pct(share)}`)
                      .join(', ');
                  return `${label}: ${shares}`;
              });
    const set = scheme.targetSet;
    const source = set
        ? `Targets from the kept set "${set.name}" (${set.source}, ${set.year}${set.reviewedOn ? `, reviewed ${set.reviewedOn}` : ''})`
        : undefined;
    return {
        headline,
        targets,
        ...(source ? { source } : {}),
        limits: scheme.limits ?? HOUSE_LIMITS,
        ...(scheme.scope ? { scope: scheme.scope.label } : {}),
    };
}

/** Why a weighting reached nobody, in words. */
const SKIP_REASON: Record<string, string> = {
    'empty-scope': 'no respondent shown is in the group it weights',
    'no-match-answers':
        'the group it matches has no answers to its questions among the respondents shown',
    'overlapping-buckets':
        'every variable it weights to has overlapping answers, so its shares do not add up to 100%',
    'no-targets': 'none of the variables it weights to is in this crosstab any more',
    'no-weights': 'no respondent shown has a usable weight in the weight variable',
};

/** The weighting a compute reports (ComputedTable.weightDiagnostics). */
type Diagnostics = NonNullable<ComputedTable['weightDiagnostics']>;

/**
 * The one-line label a page or slide carries (X91): "Weighted: <the note>", "Unweighted (natural
 * fallout)", or "Unweighted (weighting not applied)" when a scheme reached nobody.
 */
export function weightingShortLabel(computed: Pick<ComputedTable, 'weightDiagnostics'>): string {
    const w = computed.weightDiagnostics;
    if (!w) return 'Unweighted (natural fallout)';
    if (w.skipped) return 'Unweighted (weighting not applied)';
    return `Weighted: ${w.note || 'scheme'}`;
}

/**
 * What the workspace warns about above the table (X85, X87): why a weighting reached nobody, or
 * what the weighted group's weights are flagged for (`weightFlags`), and any custom-variable target
 * left out. Empty when there is nothing to say. Every threshold is `weight-limits.ts`'s.
 */
export function weightingNotices(w: Diagnostics | undefined): string[] {
    if (!w) return [];
    if (w.skipped) {
        const reason = SKIP_REASON[w.skipped] ?? 'it could not be applied';
        return [`Weighting is not applied: ${reason}. Check your filters, surveys and scheme.`];
    }
    return [
        ...(w.overlapping?.length
            ? [
                  `Not weighted to ${w.overlapping.join(', ')}: its answers overlap, so its shares do not add up to 100%.`,
              ]
            : []),
        ...(w.flags ?? []).map((f) => f.message),
    ];
}

/**
 * The lines every table and file prints about its weighting (X91), in a fixed order: the
 * headline, each variable's targets, where they came from, that the Total stays unweighted when
 * one group is weighted (decision 7), the weighting efficiency, the limits and any flags. A table
 * that is not weighted says so, as natural fallout; one whose scheme could not be applied says why.
 */
export function weightingDisclosureLines(
    computed: Pick<ComputedTable, 'weightDiagnostics'>,
): string[] {
    const w: Diagnostics | undefined = computed.weightDiagnostics;
    if (!w) return [UNWEIGHTED_DISCLOSURE];
    if (w.skipped) {
        const reason = SKIP_REASON[w.skipped] ?? 'it could not be applied';
        return [
            `Unweighted: the weighting "${w.note || 'scheme'}" was not applied because ${reason}.`,
        ];
    }
    const d = w.disclosure;
    const lines = d ? [d.headline, ...d.targets] : [`Weighted: ${w.note || 'scheme'}`];
    if (d?.source) lines.push(d.source);
    if (d?.scope) {
        lines.push(
            `Only ${d.scope} is weighted: the Total column is unweighted, and every other column counts ${d.scope}'s respondents at their weights and everyone else at 1.`,
        );
    }
    if (w.overlapping?.length) {
        lines.push(
            `Not weighted to ${w.overlapping.join(', ')}: its answers overlap, so its shares do not add up to 100%.`,
        );
    }
    if (w.stats) lines.push(`${efficiencyLine(w.stats)}.`);
    lines.push(`${limitsLine(d?.limits ?? HOUSE_LIMITS)}.`);
    for (const f of w.flags ?? []) lines.push(`Flag: ${f.message}`);
    return lines;
}

// ── What the compute is given ────────────────────────────────────────────────

type WeightInputs = Pick<
    ComputeOpts,
    | 'weightTargets'
    | 'weightMethod'
    | 'weightScope'
    | 'weightMatch'
    | 'weightNote'
    | 'weightRules'
    | 'weightVariable'
    | 'weightLimits'
    | 'weightDisclosure'
>;

/**
 * What the compute is given for a scheme: its targets as proportions (the scheme holds percents),
 * the method, the two groups' buckets, the note, and for M4S-5777 the weight variable, the limits
 * it is flagged against and the disclosure every table prints. Nothing for no scheme.
 * `settings.weighted` still decides whether any of it is applied (`buildComputeOpts`).
 */
export function weightComputeInputs(scheme: WeightScheme | null): WeightInputs {
    if (!scheme) return {};
    const fromVariable = scheme.method === 'variable' && !!scheme.weightVariable;
    if (!scheme.variables.length && !fromVariable) return {};
    return {
        weightTargets: fromVariable
            ? undefined
            : scheme.variables.map((v) => ({
                  questionId: v.variableId,
                  targets: Object.fromEntries(
                      Object.entries(v.targets).map(([cat, share]) => [cat, share / 100]),
                  ),
                  ...(v.members ? { members: v.members } : {}),
              })),
        weightMethod: scheme.method,
        ...(scheme.scope ? { weightScope: scheme.scope.buckets } : {}),
        ...(scheme.matchTo ? { weightMatch: scheme.matchTo.buckets } : {}),
        weightNote: weightingNote(scheme),
        ...(scheme.rules === CONDITION_RULES ? { weightRules: CONDITION_RULES } : {}),
        ...(fromVariable ? { weightVariable: scheme.weightVariable } : {}),
        ...(scheme.limits ? { weightLimits: scheme.limits } : {}),
        weightDisclosure: weightingDisclosure(scheme),
    };
}

// ── Several named schemes (X83) ──────────────────────────────────────────────

/** No schemes. */
export const EMPTY_WEIGHT_SCHEMES: WeightSchemeSet = Object.freeze({
    schemes: [],
    activeId: null,
}) as WeightSchemeSet;

/** The id a scheme saved before X83 is read under. */
export const LEGACY_SCHEME_ID = 'ws_legacy';
/** The name it is given. */
export const LEGACY_SCHEME_NAME = 'Weighting scheme';

/** The record block (`ai_layout.state.weighting`). Version 1. */
export interface WeightingRecord {
    v: 1;
    schemes: NamedWeightScheme[];
    activeId: string | null;
    /** X90: show the unweighted figures beside the weighted ones. Stored only when true. */
    showUnweighted?: true;
}

/** A scheme as one of the set's: it must parse and carry an id and a name. */
function parseNamed(raw: unknown): NamedWeightScheme | null {
    const s = parseWeightScheme(raw);
    if (!s || !s.id || !s.name) return null;
    return s as NamedWeightScheme;
}

/**
 * The crosstab's schemes, from its record block, else lifted from a scheme saved before X83 (one
 * scheme, `ws_legacy`, active), else none. A malformed entry is dropped, a repeated id keeps its
 * first, and an active id the list does not hold reads as none active. Stored order is kept: it is
 * the order the person made them in.
 */
export function parseWeightSchemeSet(record: unknown, legacyScheme?: unknown): WeightSchemeSet {
    if (isRecord(record) && record.v === 1 && Array.isArray(record.schemes)) {
        const schemes: NamedWeightScheme[] = [];
        for (const raw of record.schemes) {
            const s = parseNamed(raw);
            if (s && !schemes.some((x) => x.id === s.id)) schemes.push(s);
        }
        const activeId =
            typeof record.activeId === 'string' && schemes.some((s) => s.id === record.activeId)
                ? record.activeId
                : null;
        return { schemes, activeId };
    }
    const legacy = parseWeightScheme(legacyScheme);
    if (!legacy) return { schemes: [], activeId: null };
    const named: NamedWeightScheme = {
        ...legacy,
        id: legacy.id ?? LEGACY_SCHEME_ID,
        name: legacy.name ?? LEGACY_SCHEME_NAME,
    };
    return { schemes: [named], activeId: named.id };
}

/** Whether the record block asks for the unweighted figures beside the weighted (X90). */
export function showUnweightedOf(record: unknown): boolean {
    return isRecord(record) && record.v === 1 && record.showUnweighted === true;
}

/** The record block for a set, or undefined when it holds nothing (nothing is stored then). */
export function weightingRecordOf(
    set: WeightSchemeSet,
    showUnweighted = false,
): WeightingRecord | undefined {
    if (!set.schemes.length && !showUnweighted) return undefined;
    const schemes = set.schemes
        .map((s) => parseNamed(s))
        .filter((s): s is NamedWeightScheme => !!s);
    const activeId = schemes.some((s) => s.id === set.activeId) ? set.activeId : null;
    return { v: 1, schemes, activeId, ...(showUnweighted ? { showUnweighted: true } : {}) };
}

/** The record block read from JSON (a POST body or the column), canonical, or undefined. */
export function parseWeightingRecord(raw: unknown): WeightingRecord | undefined {
    if (!isRecord(raw) || raw.v !== 1 || !Array.isArray(raw.schemes)) return undefined;
    return weightingRecordOf(parseWeightSchemeSet(raw), showUnweightedOf(raw));
}

/** The scheme the tables use: the active one, or null. The ONE derivation of it. */
export function activeScheme(set: WeightSchemeSet): NamedWeightScheme | null {
    return set.schemes.find((s) => s.id === set.activeId) ?? null;
}

/** A fresh scheme id. `random` is a seam for tests. */
export function newSchemeId(random: () => number = Math.random): string {
    return `ws_${Date.now().toString(36)}${Math.floor(random() * 1e9).toString(36)}`;
}

/** A name nobody in the set has: `base`, else `base 2`, `base 3`… */
export function uniqueSchemeName(set: WeightSchemeSet, base: string, exceptId?: string): string {
    const taken = new Set(
        set.schemes.filter((s) => s.id !== exceptId).map((s) => s.name.trim().toLowerCase()),
    );
    const clean = base.trim().slice(0, MAX_NAME) || 'Weighting scheme';
    if (!taken.has(clean.toLowerCase())) return clean;
    for (let i = 2; ; i++) {
        const next = `${clean} ${i}`;
        if (!taken.has(next.toLowerCase())) return next;
    }
}

/**
 * The set with `scheme` saved: replacing the scheme of the same id in place, else added last.
 * `activate` makes it the one the tables use.
 */
export function upsertScheme(
    set: WeightSchemeSet,
    scheme: NamedWeightScheme,
    { activate }: { activate: boolean },
): WeightSchemeSet {
    const parsed = parseNamed(scheme);
    if (!parsed) return set;
    const at = set.schemes.findIndex((s) => s.id === parsed.id);
    const schemes =
        at === -1 ? [...set.schemes, parsed] : set.schemes.map((s, i) => (i === at ? parsed : s));
    return { schemes, activeId: activate ? parsed.id : set.activeId };
}

/** The set without scheme `id`. Removing the active one makes none active. */
export function removeScheme(set: WeightSchemeSet, id: string): WeightSchemeSet {
    return {
        schemes: set.schemes.filter((s) => s.id !== id),
        activeId: set.activeId === id ? null : set.activeId,
    };
}

/** A copy of scheme `id`, added last with a new id and "(copy)" name, not active. */
export function duplicateScheme(
    set: WeightSchemeSet,
    id: string,
    newId: string = newSchemeId(),
): WeightSchemeSet {
    const from = set.schemes.find((s) => s.id === id);
    if (!from) return set;
    const copy: NamedWeightScheme = {
        ...from,
        id: newId,
        name: uniqueSchemeName(set, `${from.name} (copy)`),
    };
    return { schemes: [...set.schemes, copy], activeId: set.activeId };
}

/** The set with `id` the one the tables use (null: none). An id it does not hold changes nothing. */
export function selectScheme(set: WeightSchemeSet, id: string | null): WeightSchemeSet {
    if (id !== null && !set.schemes.some((s) => s.id === id)) return set;
    return { ...set, activeId: id };
}

/**
 * Cancel (X34): the set with the scheme the table was run with put back as the active one. Its
 * entry (by id) takes back the run's content, so an edit since the run is undone; one deleted
 * since is added back; no scheme at the run means none active. Other schemes, and edits to them,
 * stay: they are not in the table.
 */
export function restoreActive(set: WeightSchemeSet, ran: WeightScheme | null): WeightSchemeSet {
    if (!ran) return set.activeId === null ? set : { ...set, activeId: null };
    const named: NamedWeightScheme = {
        ...ran,
        id: ran.id ?? LEGACY_SCHEME_ID,
        name: ran.name ?? LEGACY_SCHEME_NAME,
    };
    return upsertScheme(set, named, { activate: true });
}

/** Every scheme passed through `fn` (an in-memory read such as the open-end net id migration). */
export function mapSchemes(
    set: WeightSchemeSet,
    fn: (scheme: NamedWeightScheme) => WeightScheme | null,
): WeightSchemeSet {
    const schemes = set.schemes.flatMap((s) => {
        const next = fn(s);
        return next ? [{ ...next, id: s.id, name: s.name } as NamedWeightScheme] : [];
    });
    return {
        schemes,
        activeId: schemes.some((s) => s.id === set.activeId) ? set.activeId : null,
    };
}

// ── Who can be weighted (X85) ────────────────────────────────────────────────

/** One group the dialog offers as who is weighted, or as the group to match. */
export interface WeightGroupOption {
    member: NestMember;
    /** "QID · answer", "Survey · wave" or "Variable · bucket". */
    title: string;
    /** Whether the group's column is in the banner (only then does a table show it). */
    inBanner: boolean;
}

/**
 * The groups the weighting dialog offers beside All respondents: an iOOH study's Unexposed and
 * Exposed first (found the way the DMA × exposure banner finds them, `exposureLayer`), then every
 * answer of every question, wave and custom-variable bucket a Nest layer could split by
 * (`nestSources`, the one home of NestMember building), the banner's first. Null exposure on a
 * study that has neither.
 */
export function weightingGroups({
    catalog,
    activeBanner,
    customVariables,
    dataCatalog,
}: {
    catalog: readonly CrosstabQuestionDto[];
    activeBanner: readonly string[];
    customVariables: readonly CustomVariable[];
    /** The rail's catalog; without it only the exposure groups are offered. */
    dataCatalog?: NestCatalog;
}): {
    exposure: { unexposed: NestMember; exposed: NestMember } | null;
    groups: WeightGroupOption[];
} {
    const layer =
        exposureLayer(catalog, activeBanner, customVariables) ??
        exposureLayer(
            catalog,
            catalog.map((q) => q.qid),
            customVariables,
        );
    const exposure = layer ? { unexposed: layer.unexposed, exposed: layer.exposed } : null;
    const banner = new Set(activeBanner.map((id) => id.toUpperCase()));
    const bannerGroups = new Set(
        customVariables
            .filter((cv) => banner.has(cv.id.toUpperCase()) && cv.cvGroupName?.trim())
            .map((cv) => cv.cvGroupName!.trim()),
    );
    const inBanner = (sourceId: string): boolean => {
        if (sourceId.startsWith('cv:')) return banner.has(sourceId.slice(3).toUpperCase());
        if (sourceId.startsWith('group:')) return bannerGroups.has(sourceId.slice(6));
        return banner.has(sourceId.toUpperCase());
    };
    const options: WeightGroupOption[] = [];
    const seen = new Set<string>();
    const push = (o: WeightGroupOption) => {
        if (seen.has(o.member.key)) return;
        seen.add(o.member.key);
        options.push(o);
    };
    if (exposure && layer) {
        const shown = inBanner(layer.source);
        push({
            member: exposure.unexposed,
            title: `${layer.label} · ${exposure.unexposed.label}`,
            inBanner: shown,
        });
        push({
            member: exposure.exposed,
            title: `${layer.label} · ${exposure.exposed.label}`,
            inBanner: shown,
        });
    }
    if (dataCatalog) {
        const sources = nestSources(dataCatalog, [...customVariables])
            .flatMap((section) => section.sources)
            .filter((s) => !s.disabled && s.members.length > 0);
        const ordered = [
            ...sources.filter((s) => inBanner(s.id)),
            ...sources.filter((s) => !inBanner(s.id)),
        ];
        for (const s of ordered) {
            const head = s.kind === 'question' ? s.id : s.kind === 'survey' ? 'Survey' : s.label;
            for (const m of s.members) {
                push({ member: m, title: `${head} · ${m.label}`, inBanner: inBanner(s.id) });
            }
        }
    }
    return { exposure, groups: options };
}

// ── What the targets can be (X86) ────────────────────────────────────────────

/** One variable the dialog offers as a weighting target. */
export interface WeightTargetSource {
    /** What the scheme stores as `variableId`: a qid, `__SURVEY_ID` or `cv:<id>`. */
    id: string;
    /** The question id, or the variable's name. */
    qid: string;
    /** The wording, or the variable's name. */
    label: string;
    /** "QID · wording", what the picker shows and searches. */
    title: string;
    cats: Array<{ id: string; label: string }>;
    kind: 'question' | 'survey' | 'variable';
}

/** The question kinds whose answers cannot be shares of a population. */
const NOT_A_TARGET = /matrix|grid|open|text|rank/i;

/**
 * Every variable a scheme can weight to (X86): each question with answers (not a matrix, open end
 * or ranking), the wave when there are several, then every custom variable whose buckets are
 * answers, its bucket labels as the categories. Each is titled "QID · wording" so the picker can
 * be searched by either.
 */
export function weightTargetSources(
    catalog: NestCatalog & { bannerCatalogIds?: string[] },
    customVariables: readonly CustomVariable[],
): WeightTargetSource[] {
    const out: WeightTargetSource[] = [];
    const survey = catalog.bannerVarDefs[SURVEY_QID];
    if (survey && (catalog.totalSurveys ?? 0) > 1 && survey.cats.length > 1) {
        out.push({
            id: SURVEY_QID,
            qid: 'Survey',
            label: 'Survey / Wave',
            title: 'Survey · Survey / Wave',
            cats: survey.cats,
            kind: 'survey',
        });
    }
    for (const q of catalog.rowQuestions) {
        if (NOT_A_TARGET.test(q.kind ?? '')) continue;
        const def = catalog.bannerVarDefs[q.qid] ?? catalog.bannerVarDefs[q.id];
        const cats = (def?.cats ?? []).filter((c) => !/^scale-\d+-\d+$/i.test(c.id));
        if (cats.length < 2) continue;
        const label = q.text.trim() || q.qid;
        out.push({
            id: q.qid,
            qid: q.qid,
            label,
            title: `${q.qid} · ${label}`,
            cats,
            kind: 'question',
        });
    }
    for (const cv of customVariables) {
        if (!hasVariableAnswers(cv)) continue;
        const name = cv.name.trim() || cv.id;
        const answers = variableAnswers(cv);
        if (answers.length < 2) continue;
        out.push({
            id: `${CV_TARGET_PREFIX}${cv.id}`,
            qid: name,
            label: name,
            title: `Variable · ${name}`,
            cats: answers.map((a) => ({ id: a, label: a })),
            kind: 'variable',
        });
    }
    return out;
}

/** One variable the 'variable' method can read weights from (X83). */
export interface WeightVariableSource {
    /** A number question's qid, or `cv:<id>` for a Formula variable. */
    id: string;
    title: string;
}

/** The question kinds that hold a number per respondent, weights included. */
const NUMBER_KINDS = /^(numeric|number|weight)$/i;

/**
 * Where weights can be read from: a Formula variable, or a question that holds a number per
 * respondent. MFour's own survey data has no weight column, so on native data this is the
 * Formula variables only; a client file brought in with its weights (X120) adds its column.
 */
export function weightVariableSources(
    catalog: Pick<NestCatalog, 'rowQuestions'>,
    customVariables: readonly CustomVariable[],
): WeightVariableSource[] {
    return [
        ...catalog.rowQuestions
            .filter((q) => NUMBER_KINDS.test(q.kind ?? ''))
            .map((q) => ({ id: q.qid, title: `${q.qid} · ${q.text.trim() || q.qid}` })),
        ...customVariables
            .filter((cv) => cv.kind === 'formula')
            .map((cv) => ({
                id: `${CV_TARGET_PREFIX}${cv.id}`,
                title: `Formula · ${cv.name.trim() || cv.id}`,
            })),
    ];
}

/**
 * What no longer matches in a scheme (X84, X86): a target variable or answer, a weight variable
 * or a group the crosstab no longer has. Shown, never dropped silently; the compute already skips
 * an answer nobody holds.
 */
export function schemeProblems(
    scheme: WeightScheme,
    sources: readonly WeightTargetSource[],
    variableSources: readonly WeightVariableSource[] = [],
): string[] {
    const out: string[] = [];
    const byId = new Map(sources.map((s) => [s.id.toUpperCase(), s]));
    for (const v of scheme.variables) {
        const src = byId.get(v.variableId.toUpperCase());
        const name = v.label ?? targetName(v);
        if (!src) {
            out.push(`${name} is not in this crosstab any more, so nothing is weighted to it.`);
            continue;
        }
        const cats = new Set(src.cats.map((c) => c.id.toUpperCase()));
        // A kept set's target covers several codes (X88): it is gone when none of them is left.
        const gone = Object.keys(v.targets).filter((c) =>
            (v.members?.[c] ?? [c]).every((code) => !cats.has(code.toUpperCase())),
        );
        if (gone.length) {
            out.push(
                `${name}: ${gone.map((c) => v.catLabels?.[c] ?? c).join(', ')} ${gone.length === 1 ? 'is' : 'are'} no longer an answer, so ${gone.length === 1 ? 'its target is' : 'their targets are'} not used.`,
            );
        }
    }
    if (
        scheme.method === 'variable' &&
        scheme.weightVariable &&
        !variableSources.some((s) => s.id === scheme.weightVariable)
    ) {
        out.push(
            `${scheme.weightVariable} is not in this crosstab any more, so no weight can be read.`,
        );
    }
    return out;
}

// ── What the compute reports (re-exported for the readers) ───────────────────

export type { WeightFlag, WeightLimits, WeightStats };
